import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { BECompletionV4 } from "@kivra/sdk/types/core/completion";
import type { GetCopyArguments } from "../../copy";
import { authAppRedirect, oldSignupUserOtp, signupUser } from ".";
import type { FetchState, OTPState } from "../../utils/use-completions";

interface AuthState {
  readonly username: string;
  readonly phoneNumber: string;
  readonly isLoading: boolean;
  readonly tunnistusData: TunnistusState;
  readonly error: ErrorState;
  // TODO: Could be changed to a boolean
  readonly terms: TermsState;
  readonly completions: BECompletionV4[];
  readonly completionFetchState: FetchState;
  readonly completionOTPState: OTPState;
  readonly passwordResetData: PasswordResetState;
}

export type TunnistusState = typeof initialTunnistusState;

export interface ErrorState {
  readonly errorCopyKey?: GetCopyArguments;
  readonly errorCode: string;
  readonly traceId: string;
}

export interface TermsState {
  readonly termsAccepted: boolean;
}

interface PasswordResetState {
  readonly newPassword: string;
  readonly verifyNewPassword: string;
}

export const initialTunnistusState = {
  isNewUser: true,
  ssn: "",
  infoSource: "",
  givenName: "",
  lastName: "",
  signature: "",
  emailAddress: "",
  resourceOwner: "",
  accessToken: "",
  tokenType: "",
};

const initialPasswordResetState = {
  newPassword: "",
  verifyNewPassword: "",
};

const initialState: AuthState = {
  username: "",
  phoneNumber: "",
  isLoading: false,
  tunnistusData: initialTunnistusState,
  // TODO: What would be the correct initial completion state?
  completions: [
    {
      type: "email",
      level: "optional",
      context: "never_verified",
      current: null,
    },
    {
      type: "phone",
      level: "optional",
      context: "never_verified",
      current: null,
    },
  ],
  completionFetchState: "INITIAL",
  completionOTPState: "INITIAL",
  error: { errorCode: "", traceId: "" },
  terms: { termsAccepted: false },
  passwordResetData: initialPasswordResetState,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setUsername(state, action: PayloadAction<string>) {
      state.username = action.payload;
    },
    setPhoneNumber(state, action: PayloadAction<string>) {
      state.phoneNumber = action.payload;
    },
    setVerificationValues(state, action: PayloadAction<TunnistusState>) {
      state.tunnistusData = action.payload;
    },
    setErrorCopyKey(state, action: PayloadAction<GetCopyArguments>) {
      state.error.errorCopyKey = action.payload;
    },
    clearErrorCopyKey(state) {
      state.error.errorCopyKey = undefined;
    },
    setError(state, action: PayloadAction<string>) {
      state.error.errorCode = action.payload;
    },
    clearError(state) {
      state.error.errorCode = "";
    },
    setTraceId(state, action: PayloadAction<string>) {
      state.error.traceId = action.payload;
    },
    clearTraceId(state) {
      state.error.traceId = "";
    },
    setTermsAccepted(state, action: PayloadAction<boolean>) {
      state.terms.termsAccepted = action.payload;
    },
    setPassword(state, action: PayloadAction<string>) {
      state.passwordResetData.newPassword = action.payload;
    },
    setVerifyPassword(state, action: PayloadAction<string>) {
      state.passwordResetData.verifyNewPassword = action.payload;
    },
    setCompletions(state, action: PayloadAction<BECompletionV4[]>) {
      state.completions = action.payload;
    },
    setCompletionFetchState(state, action: PayloadAction<FetchState>) {
      state.completionFetchState = action.payload;
    },
    setCompletionOTPState(state, action: PayloadAction<OTPState>) {
      state.completionOTPState = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(signupUser.pending, oldSignupUserOtp.pending),
        (state) => {
          state.isLoading = true;
        },
      )
      // TODO: This could probably be done for the fetch handling as well
      .addMatcher(
        isAnyOf(
          signupUser.fulfilled,
          signupUser.rejected,
          oldSignupUserOtp.fulfilled,
          oldSignupUserOtp.rejected,
          authAppRedirect.fulfilled,
          authAppRedirect.rejected,
        ),
        (state) => {
          state.isLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          signupUser.pending,
          oldSignupUserOtp.pending,
          authAppRedirect.pending,
        ),
        (state) => {
          state.error = initialState.error;
        },
      );
  },
});

export const {
  setUsername,
  setPhoneNumber,
  setVerificationValues,
  setErrorCopyKey,
  clearErrorCopyKey,

  clearError,
  setTraceId,
  clearTraceId,
  setTermsAccepted,
  setPassword,
  setVerifyPassword,
  setCompletions,
  setCompletionFetchState,
  setCompletionOTPState,
} = authSlice.actions;
export const authReducer = authSlice.reducer;
